import { useEffect, useRef, useState } from "react";
import { Trophy } from "lucide-react";
import { useTranslation } from "../../../i18n";
import { SCORE_EVENT, getSessionScore, getStoredScore } from "../../helpers/sessionScore";
import { playMilestoneChime, playMilestoneFanfare } from "../../helpers/audioPlayer";
import { celebrationSoundEnabled } from "../pointsSettings";
import {
    TIER_DURATION_MS,
    crossedMilestone,
    isGrandMilestone,
    type Milestone,
} from "../milestones";
import "../../../styles/gamification.css";

/** Firework bursts for the full-screen tier, as [left%, top%, delay ms]. */
const BURSTS: [number, number, number][] = [
    [22, 30, 0],
    [78, 26, 160],
    [50, 16, 320],
    [14, 62, 460],
    [86, 60, 600],
    [38, 48, 760],
    [64, 52, 900],
];

const SPARKS_PER_BURST = 12;

/**
 * Watches the running daily score and reports the milestone just crossed.
 *
 * Mounted once, app-wide: sessionScore already broadcasts every change on the
 * SCORE_EVENT window event, so a single listener covers all eleven scoring
 * screens without any of them knowing this exists.
 *
 * The baseline rule is the subtle part. On login, refreshScoreFromServer()
 * replays the day's total as one event — an operator returning from break on
 * 6,400 points would otherwise be met with every rung at once. So the FIRST
 * value seen after mount only sets the baseline; celebrations start from the
 * next real increase.
 */
export const useMilestoneCelebration = (): { milestone: Milestone | null; bumpKey: number } => {
    const [milestone, setMilestone] = useState<Milestone | null>(null);
    const [bumpKey, setBumpKey] = useState(0);
    // undefined = no baseline yet. Deliberately not 0: a genuine 0 is a real
    // baseline (fresh day) and must not be re-armed on the next event.
    const prevRef = useRef<number | undefined>(undefined);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Seed from whatever is already banked locally, so a page refresh
        // mid-shift doesn't replay the milestones already celebrated today.
        // Null means this device has nothing for today — NOT a zero: seeding 0
        // there would make the login refresh's replay of the day's total read
        // as one enormous climb and fire every rung at once.
        prevRef.current = getStoredScore() ?? undefined;

        const handler = (event: Event) => {
            const detail = (event as CustomEvent<number>).detail;
            const next = typeof detail === "number" ? detail : getSessionScore();
            const prev = prevRef.current;
            prevRef.current = next;
            if (prev === undefined) return;

            const crossed = crossedMilestone(prev, next);
            if (!crossed) return;

            setMilestone(crossed);
            setBumpKey(key => key + 1);

            if (celebrationSoundEnabled()) {
                if (crossed.tier === 3) playMilestoneFanfare();
                else if (crossed.tier === 2) playMilestoneChime();
            }

            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(
                () => setMilestone(null),
                TIER_DURATION_MS[crossed.tier],
            );
        };

        window.addEventListener(SCORE_EVENT, handler as EventListener);
        return () => {
            window.removeEventListener(SCORE_EVENT, handler as EventListener);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    return { milestone, bumpKey };
};

/**
 * The full-screen half of the milestone ladder (tier 3 — 5,000 and above).
 *
 * Fixed overlay with pointer-events: none, the same shape as ScanFlashOverlay,
 * so a scan landing mid-celebration passes straight through to the page
 * underneath. Nothing here may ever swallow a keystroke: the scanner is a
 * keyboard, and the operator is still working.
 *
 * Tiers 1 and 2 are not drawn here — they belong at the score pill, where the
 * number they're about is (see ScoreDisplay).
 */
const MilestoneCelebration = () => {
    const { t } = useTranslation();
    const { milestone, bumpKey } = useMilestoneCelebration();

    const active = milestone !== null && milestone.tier === 3;
    const grand = milestone !== null && isGrandMilestone(milestone.value);

    return (
        <div
            className={
                "gamif-milestone-overlay"
                + (active ? " gamif-milestone-overlay--active" : "")
                + (grand ? " gamif-milestone-overlay--grand" : "")
            }
            aria-hidden={!active}
            data-testid="milestone-overlay"
        >
            {active && (
                <div key={bumpKey} className="gamif-milestone-inner">
                    {BURSTS.map(([left, top, delay], b) => (
                        <div
                            key={b}
                            className="gamif-firework"
                            style={{ left: `${left}%`, top: `${top}%`, animationDelay: `${delay}ms` }}
                        >
                            {Array.from({ length: SPARKS_PER_BURST }, (_, i) => (
                                <span
                                    key={i}
                                    className={`gamif-firework-spark gamif-firework-spark-${(i % 6) + 1}`}
                                    style={{
                                        // Direction and throw distance as custom
                                        // properties so one keyframe serves all
                                        // 84 sparks instead of 84 keyframes.
                                        ["--spark-angle" as any]: `${(360 / SPARKS_PER_BURST) * i}deg`,
                                        ["--spark-distance" as any]: `${9 + (i % 3) * 2}vmin`,
                                        animationDelay: `${delay}ms`,
                                    }}
                                />
                            ))}
                        </div>
                    ))}

                    <div className="gamif-milestone-card">
                        <Trophy className="gamif-milestone-icon" aria-hidden="true" />
                        <span className="gamif-milestone-value">
                            {milestone.value.toLocaleString()}
                        </span>
                        <span className="gamif-milestone-label">{t("score_label")}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MilestoneCelebration;
